import React, { useEffect } from 'react';

import {
  AutocompleteArrayInput,
  RadioButtonGroupInput,
  ReferenceArrayInput,
  useGetOne,
} from 'react-admin';

import {
  Box,
  Grid,
  Grow,
  Typography,
} from '@mui/material';

import { useFormContext, useWatch } from 'react-hook-form';

import Label from '../../../customFields/Label';
import ruleTypes from '../../../constants/ruleTypes';
import resourceNames from '../../../constants/resourceNames';
import { requestTypes, RequestTypes } from '../../../constants/requestTypes';
import assignmentTrigger, { AssignmentTrigger, assignmentTriggerChoices } from '../../../constants/assignmentTrigger';
import {
  TRANSACTION_TYPES_ASSIGNMENT_INFO,
  TRANSACTION_TYPES_ASSIGNMENT_TITLE,
} from '../../../constants/transactionTypesAssignment';

const SOURCE = 'transactionTypesAssignment';

const ASSIGNING_TRIGGERS: AssignmentTrigger[] = [
  assignmentTrigger.ON_EXECUTION,
  assignmentTrigger.ON_TRIGGER,
  assignmentTrigger.ON_PING,
];

type FormValues = {
  ruleType: string;
  process?: string;
  transactionTypesAssignment?: {
    assignmentTrigger?: AssignmentTrigger;
    transactionTypesToAdd?: string[];
    transactionTypesToRemove?: string[];
  };
}

const TransactionTypesAssignment = () => {
  const { setValue } = useFormContext<FormValues>();
  const ruleType = useWatch<FormValues, 'ruleType'>({ name: 'ruleType' });
  const process = useWatch<FormValues, 'process'>({ name: 'process' });
  const assignment = useWatch<FormValues, 'transactionTypesAssignment'>({ name: SOURCE });

  const { data: processData, isPending } = useGetOne<{
    id: string;
    requestType: RequestTypes;
  }>(resourceNames.PROCESSES, { id: process ?? '' }, { enabled: !!process });

  // Only a request rule on a transaction process may assign transaction types (ADR-0010). The
  // request type is only known once the process is loaded, so until then the rule is neither
  // eligible nor known to be ineligible and we must not touch an already saved configuration.
  const hasProcess = ruleType === ruleTypes.REQUEST && !!process;
  const isEligible = hasProcess && processData?.requestType === requestTypes.TRANSACTION;
  const isRequestTypeUnknown = hasProcess && isPending;

  useEffect(() => {
    if (isEligible || isRequestTypeUnknown || !assignment) return;
    setValue(SOURCE, undefined);
  }, [isEligible, isRequestTypeUnknown, assignment, setValue]);

  if (!isEligible) return null;

  const isAssigning = ASSIGNING_TRIGGERS.some(
    (trigger) => trigger === assignment?.assignmentTrigger,
  );

  return (
    <Grid size={12} data-testid={SOURCE}>
      <Grow in>
        <Grid container>
          <Grid size={12}>
            <Label
              variant="label"
              title={TRANSACTION_TYPES_ASSIGNMENT_TITLE}
              info={TRANSACTION_TYPES_ASSIGNMENT_INFO}
            />
          </Grid>
          <Grid container marginY={2} size={12}>
            <Grid size={12}>
              <Box display="flex" flexDirection="column">
                <Typography variant="label">
                  On what event would you like to add or remove transaction types on the
                  transaction?
                </Typography>
                <RadioButtonGroupInput
                  label={false}
                  defaultValue={assignmentTrigger.NONE}
                  source={`${SOURCE}.assignmentTrigger`}
                  choices={assignmentTriggerChoices}
                  helperText={false}
                />
              </Box>
            </Grid>
            {isAssigning && (
              <Grid
                container
                padding={4}
                sx={{ backgroundColor: 'greenGray.main', borderRadius: 2 }}
                size={12}
              >
                <Grid marginY={1} size={12}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="label">
                      Specify the transaction types to add to the transaction
                    </Typography>
                    <ReferenceArrayInput
                      perPage={200}
                      filter={{ active: true }}
                      reference={resourceNames.TRANSACTION_TYPES}
                      source={`${SOURCE}.transactionTypesToAdd`}
                    >
                      <AutocompleteArrayInput
                        fullWidth
                        filterToQuery={(q) => ({ name: q })}
                        source="name"
                        label="Transaction types to add"
                        optionText="name"
                        helperText={false}
                      />
                    </ReferenceArrayInput>
                  </Box>
                </Grid>
                <Grid marginY={1} size={12}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="label">
                      Specify the transaction types to remove from the transaction
                    </Typography>
                    <ReferenceArrayInput
                      perPage={200}
                      filter={{ active: true }}
                      reference={resourceNames.TRANSACTION_TYPES}
                      source={`${SOURCE}.transactionTypesToRemove`}
                    >
                      <AutocompleteArrayInput
                        fullWidth
                        filterToQuery={(q) => ({ name: q })}
                        source="name"
                        label="Transaction types to remove"
                        optionText="name"
                        helperText={false}
                      />
                    </ReferenceArrayInput>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grow>
    </Grid>
  );
};

export default TransactionTypesAssignment;
